import React, { useRef, useMemo, useEffect, useState } from 'react';

import { DiskBadge } from 'apps/auth/components/common/DiskBadge/DiskBadge';
import { HeaderPanel } from 'apps/auth/components/common/Layout/HeaderPanel/HeaderPanel';
import { Row } from 'apps/auth/components/common/Layout/Row';
import { Panel } from 'apps/auth/components/common/Panel/Panel';
import { DiskBadgeWrapper } from 'apps/auth/components/common/Panel/styles';
import PopupButton from 'apps/auth/components/common/Popup/PopupButton';
import SimplePopup from 'apps/auth/components/common/Popup/SimplePopup';
import { Text } from 'apps/auth/components/common/Text/Text';
import InputText from 'apps/auth/components/form-multi/InputText';
import { CATEGORY_ACCOUNT_DEBIT_VIA_MODULR, CATEGORY_ACCOUNT_DEBIT_VIA_PLAID, CATEGORY_TRANSFER_FROM_BANK_ACCOUNT } from 'apps/auth/constants/Constants';
import { AUTH_SEND_PLAID_SEARCH } from 'apps/auth/constants/UrlConstants';
import { apiFetchData } from 'apps/auth/helpers/api';
import { timer } from 'apps/auth/helpers/utils';
import { useExtra, usePageMessages } from 'apps/auth/hooks/selectors';

import PlaidBankItem from './PlaidBankItem';

const FORM_NAME = 'searchPlaidBank';

const PlaidBankList = ({ popupRef, actualRef, modalRef, onClickHandler, changePaymentMethod, searchField }) => {
    const timerID = useRef(null);
    const { availablePaymentMethodsList, canUseBankTransferMethod, banks } = useExtra();
    const { plaidPopupTitle, plaidPopupText, payBankTransferButton, popularTitle, otherTitle, bankNotInList, selectBankTitle } = usePageMessages();

    const [searchQuery, setSearchQuery] = useState('');
    const [newBanks, setNewBanks] = useState(banks);

    useEffect(() => {
        return () => {
            if (timerID?.current) {
                clearTimeout(timerID.current);
            }
        };
    }, []);

    const canShowBankTransferMethod = (availablePaymentMethodsList, canUseBankTransferMethod) => {
        if (canUseBankTransferMethod && availablePaymentMethodsList && availablePaymentMethodsList[CATEGORY_ACCOUNT_DEBIT_VIA_PLAID] != null) {
            return true;
        }
        return !!(canUseBankTransferMethod && availablePaymentMethodsList && availablePaymentMethodsList[CATEGORY_TRANSFER_FROM_BANK_ACCOUNT] != null);
    };

    const clickBankTransferMethod = (availablePaymentMethodsList = {}) => {
        let actualBankTransferMethod;
        if (availablePaymentMethodsList[CATEGORY_ACCOUNT_DEBIT_VIA_MODULR] != null) {
            actualBankTransferMethod = availablePaymentMethodsList[CATEGORY_ACCOUNT_DEBIT_VIA_MODULR];
        } else if (availablePaymentMethodsList[CATEGORY_TRANSFER_FROM_BANK_ACCOUNT] != null) {
            actualBankTransferMethod = availablePaymentMethodsList[CATEGORY_TRANSFER_FROM_BANK_ACCOUNT];
        }

        const getPlaidPopupText = (
            <>
                <p>{plaidPopupTitle}</p>
                <p>{plaidPopupText}</p>
            </>
        );
        popupRef.current?.open(
            <SimplePopup text={getPlaidPopupText} popupRef={popupRef}>
                <PopupButton
                    iconName="arrow-forward"
                    text={payBankTransferButton}
                    onClick={(e) => {
                        e.preventDefault();
                        popupRef.current?.close();
                        changePaymentMethod(actualBankTransferMethod?.code, () => {
                            modalRef?.current?.close();
                            actualRef.current.click();
                        });
                    }}
                />
            </SimplePopup>,
        );
    };

    const debouncedSearch = (val) => (timerID.current = timer(timerID.current, () => loadData(val), 500));

    const popularBankList = useMemo(() => {
        return banks?.filter((f) => f?.isPopular)?.map((bank) => <PlaidBankItem key={bank?.id} bank={bank} onClickHandler={onClickHandler} />);
    }, [banks, onClickHandler]);

    const otherBankList = useMemo(() => {
        return banks?.map((bank) => <PlaidBankItem key={bank?.id} bank={bank} onClickHandler={onClickHandler} />);
    }, [banks, onClickHandler]);

    const searchBankList = useMemo(() => {
        return newBanks?.map((bank) => <PlaidBankItem key={bank?.id} bank={bank} onClickHandler={onClickHandler} query={searchQuery} />);
    }, [newBanks, onClickHandler]);

    const popularBanks = (
        <>
            {popularBankList && popularBankList.length > 0 && (
                <Row indent="adaptive-32-48">
                    <Text variant="div" color="secondary" size="subheader" indent="adaptive-32-48">
                        {popularTitle}
                    </Text>
                    {popularBankList}
                </Row>
            )}

            {otherBankList && (
                <>
                    <Text variant="div" color="secondary" size="subheader" indent="adaptive-32-48">
                        {otherTitle}
                    </Text>
                    {otherBankList}
                </>
            )}
        </>
    );

    const loadData = (value) => {
        setSearchQuery(value);
        const payload = {
            search: {
                value: value,
                name: 'search',
            },
        };
        apiFetchData(AUTH_SEND_PLAID_SEARCH, payload).then((json) => {
            if (json.extra) {
                setNewBanks(json.extra.banks);
            }
        });
    };

    return (
        <>
            <HeaderPanel title={selectBankTitle} />
            {searchField && (
                <Row indent="adaptive-32-48">
                    <InputText formName={FORM_NAME} field={searchField} onChangeHandler={debouncedSearch} isNewInput focus value={searchQuery} />
                </Row>
            )}
            {banks && <>{searchQuery ? searchBankList : popularBanks}</>}
            {canShowBankTransferMethod(availablePaymentMethodsList, canUseBankTransferMethod) && (
                <Panel
                    type="button"
                    onClick={() => {
                        clickBankTransferMethod(availablePaymentMethodsList);
                    }}
                    isHoverable
                    negativeMargin
                >
                    <DiskBadgeWrapper>
                        <DiskBadge variant="secondary" iconName="bank" />
                    </DiskBadgeWrapper>
                    <Text color="primary">{bankNotInList}</Text>
                </Panel>
            )}
        </>
    );
};

export default PlaidBankList;
